/**
 * Frontend auth helpers. Cookies are HttpOnly so JS can't read them
 * directly — instead we ask the server "am I logged in?" via /me.
 */

export interface AuthState {
  authenticated: boolean;
  /** False when the server has no AUTH_PASSWORD_HASH set — auth is off. */
  gateEnabled: boolean;
  /** Multi-channel feature flag (CHANNELS_ENABLED on the server). When
   *  false the app renders the original single-brand V1 UI. */
  channelsEnabled?: boolean;
  /** True when MFA is enrolled → the login screen must show the code field
   *  alongside the password. Disclosed publicly (unauthenticated) for that. */
  mfaRequired?: boolean;
  /** Same value, present only on an authenticated response — drives the
   *  Settings Security card's ON/OFF state. */
  mfaEnrolled?: boolean;
}

export async function checkAuth(): Promise<AuthState> {
  const r = await fetch("/api/auth/me", { credentials: "same-origin" });
  if (!r.ok) return { authenticated: false, gateEnabled: true, channelsEnabled: false };
  return (await r.json()) as AuthState;
}

export type LoginResult =
  | { ok: true }
  | { ok: false; error: string; retryAfterSec?: number; mfaRequired?: boolean };

/**
 * `otp` is the 6-digit authenticator code — only needed when MFA is enrolled
 * and this browser has no device-trust cookie. The server signals that case
 * with `mfaRequired: true` so the login screen can reveal the code field and
 * resubmit password + code together.
 */
export async function login(password: string, otp?: string): Promise<LoginResult> {
  const r = await fetch("/api/auth/login", {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(otp ? { password, otp } : { password }),
  });
  if (r.ok) return { ok: true };
  const body = (await r.json().catch(() => ({}))) as { error?: string; mfaRequired?: boolean };
  const retry = r.headers.get("retry-after");
  return {
    ok: false,
    error: body.error || `${r.status} ${r.statusText}`,
    retryAfterSec: retry ? Number(retry) : undefined,
    mfaRequired: body.mfaRequired === true,
  };
}

/* ------------------------------- MFA management ------------------------------- */

async function postJson<T>(url: string, body?: unknown): Promise<T> {
  const r = await fetch(url, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!r.ok) {
    const err = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(err.error || `${r.status} ${r.statusText}`);
  }
  return r.json() as Promise<T>;
}

/** Start MFA enrollment: returns the base32 secret + otpauth URI to QR-encode. */
export async function mfaSetup(): Promise<{ secret: string; uri: string }> {
  return postJson("/api/auth/mfa/setup");
}

/** Confirm enrollment with a code from the authenticator app. */
export async function mfaEnable(code: string): Promise<void> {
  await postJson("/api/auth/mfa/enable", { code });
}

/** Turn MFA off — requires a current code. */
export async function mfaDisable(code: string): Promise<void> {
  await postJson("/api/auth/mfa/disable", { code });
}

export async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" });
}

/**
 * Window-level event used by api.ts to tell the app shell that a 401
 * came back from a request and we should bounce to the login screen.
 * Components can listen for this to clear local UI state.
 */
export const AUTH_EXPIRED_EVENT = "ql:auth-expired";

export function emitAuthExpired(): void {
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}
